import React, { useState } from "react";
import styled from "styled-components";
import { RebelColors, RebelModal, RebelModalHeader, RebelModalBody, RebelModalFooter, RebelAccentButton, RebelSecondaryButton, RebelLabel, RebelInput } from "../../../styles/rebel-experiences";
import emailjs from "@emailjs/browser";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

const EMAILJS_SERVICE_ID = "service_xiqdcy8";
const EMAILJS_SHARE_TEMPLATE_ID = "SHARE_TEMPLATE_ID"; // TODO: Replace after creating in EmailJS dashboard
const EMAILJS_PUBLIC_KEY = "user_8Qt3ZJv9dlnGhiJefkhBh";

const RebelBudgetShareModal = ({ isOpen, toggle, config, properties, budgetId, savedEmail }) => {
    const [emailInput, setEmailInput] = useState("");
    const [emails, setEmails] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [sentSuccess, setSentSuccess] = useState(false);
    const [sentCount, setSentCount] = useState(0);

    const viewLink = `${window.location.origin}/budget-app/rebel-budget/view/${budgetId}`;

    const addEmail = () => {
        const trimmed = emailInput.trim();
        if (trimmed && trimmed.includes("@") && !emails.includes(trimmed)) {
            setEmails(prev => [...prev, trimmed]);
            setEmailInput("");
        }
    };

    const removeEmail = (email) => {
        setEmails(prev => prev.filter(e => e !== email));
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            addEmail();
        }
    };

    const handleSend = async () => {
        if (emails.length === 0) return;

        setIsSending(true);

        const generatedDate = new Date().toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });

        let successCount = 0;

        for (const recipientEmail of emails) {
            try {
                await emailjs.send(EMAILJS_SERVICE_ID, EMAILJS_SHARE_TEMPLATE_ID, {
                    to_email: recipientEmail,
                    sender_email: savedEmail || "A team member",
                    company_name: config.companyName || "Your Team",
                    start_date: config.startDate || "TBD",
                    end_date: config.endDate || "TBD",
                    attendees: config.attendees,
                    num_properties: properties.length,
                    review_link: viewLink,
                    generated_date: generatedDate,
                }, EMAILJS_PUBLIC_KEY);
                successCount++;
            } catch (error) {
                console.warn(`Failed to send share email to ${recipientEmail}:`, error);
            }
        }

        setSentCount(successCount);
        setSentSuccess(true);
        setIsSending(false);
    };

    const handleClose = () => {
        setSentSuccess(false);
        setSentCount(0);
        setEmails([]);
        setEmailInput("");
        toggle();
    };

    if (sentSuccess) {
        return (
            <RebelModal isOpen={isOpen} toggle={handleClose}>
                <RebelModalHeader>Invitations Sent</RebelModalHeader>
                <RebelModalBody>
                    <SuccessMessage>
                        <FontAwesomeIcon icon={['fas', 'check-circle']} style={{ marginRight: 8 }} />
                        {sentCount} invitation{sentCount !== 1 ? 's' : ''} sent successfully!
                    </SuccessMessage>
                    <LinkContainer>
                        <RebelLabel>View-only link shared:</RebelLabel>
                        <LinkDisplay>{viewLink}</LinkDisplay>
                        <CopyButton onClick={() => navigator.clipboard.writeText(viewLink)}>
                            Copy Link
                        </CopyButton>
                    </LinkContainer>
                    <InfoNote>
                        Recipients can view property details and submit their rankings, but cannot edit the comparison.
                    </InfoNote>
                </RebelModalBody>
                <RebelModalFooter>
                    <RebelAccentButton onClick={handleClose}>Done</RebelAccentButton>
                </RebelModalFooter>
            </RebelModal>
        );
    }

    return (
        <RebelModal isOpen={isOpen} toggle={handleClose}>
            <RebelModalHeader>Share with Team</RebelModalHeader>
            <RebelModalBody>
                <p style={{ color: RebelColors.textMuted, marginBottom: '1rem', fontSize: '0.9rem' }}>
                    Send a link to team members so they can review properties and submit their rankings.
                </p>

                <EmailInputRow>
                    <EmailInputWrapper>
                        <RebelInput
                            type="email"
                            value={emailInput}
                            onChange={e => setEmailInput(e.target.value)}
                            onKeyDown={handleKeyDown}
                            placeholder="colleague@company.com"
                        />
                    </EmailInputWrapper>
                    <AddButton onClick={addEmail} disabled={!emailInput.trim() || !emailInput.includes("@")}>
                        Add
                    </AddButton>
                </EmailInputRow>

                {emails.length > 0 && (
                    <EmailChipsContainer>
                        {emails.map(email => (
                            <EmailChip key={email}>
                                <span>{email}</span>
                                <RemoveChip onClick={() => removeEmail(email)}>
                                    <FontAwesomeIcon icon={['fas', 'times']} />
                                </RemoveChip>
                            </EmailChip>
                        ))}
                    </EmailChipsContainer>
                )}

                {emails.length === 0 && (
                    <EmptyHint>Add email addresses above to send invitations.</EmptyHint>
                )}
            </RebelModalBody>
            <RebelModalFooter>
                <RebelSecondaryButton onClick={handleClose}>Cancel</RebelSecondaryButton>
                <RebelAccentButton onClick={handleSend} disabled={emails.length === 0 || isSending}>
                    {isSending ? 'Sending...' : `Send ${emails.length > 0 ? emails.length : ''} Invite${emails.length !== 1 ? 's' : ''}`}
                </RebelAccentButton>
            </RebelModalFooter>
        </RebelModal>
    );
};

export default RebelBudgetShareModal;

const SuccessMessage = styled.p`
    color: #10b981;
    font-size: 1.1rem;
    font-weight: 600;
    text-align: center;
    margin-bottom: 1.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
`;

const LinkContainer = styled.div`
    margin-bottom: 1rem;
`;

const LinkDisplay = styled.div`
    background-color: ${RebelColors.gray700};
    border: 1px solid ${RebelColors.gold};
    border-radius: 6px;
    padding: 10px 12px;
    color: ${RebelColors.gold};
    font-size: 0.85rem;
    word-break: break-all;
    margin-bottom: 0.5rem;
`;

const CopyButton = styled.button`
    background: transparent;
    border: 1px solid ${RebelColors.gold};
    color: ${RebelColors.gold};
    border-radius: 4px;
    padding: 4px 12px;
    font-size: 0.8rem;
    cursor: pointer;
    transition: all 0.2s ease;

    &:hover {
        background: ${RebelColors.gold};
        color: #000;
    }
`;

const InfoNote = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.8rem;
    text-align: center;
    font-style: italic;
    margin-top: 1rem;
    margin-bottom: 0;
`;

const EmailInputRow = styled.div`
    display: flex;
    gap: 8px;
    margin-bottom: 12px;
`;

const EmailInputWrapper = styled.div`
    flex: 1;
`;

const AddButton = styled.button`
    background: ${props => props.disabled ? RebelColors.gray600 : RebelColors.gold};
    color: ${props => props.disabled ? RebelColors.textMuted : '#000'};
    border: none;
    border-radius: 0.5rem;
    padding: 0 16px;
    font-weight: 600;
    font-size: 0.875rem;
    cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
    transition: all 0.2s ease;
    opacity: ${props => props.disabled ? 0.5 : 1};

    &:hover:not(:disabled) {
        opacity: 0.9;
    }
`;

const EmailChipsContainer = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 8px;
    margin-top: 8px;
`;

const EmailChip = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    background: rgba(253, 181, 6, 0.1);
    border: 1px solid rgba(253, 181, 6, 0.3);
    border-radius: 20px;
    padding: 4px 10px 4px 14px;
    font-size: 0.85rem;
    color: ${RebelColors.text};
`;

const RemoveChip = styled.button`
    background: none;
    border: none;
    color: ${RebelColors.textMuted};
    cursor: pointer;
    padding: 2px;
    font-size: 0.7rem;
    display: flex;
    align-items: center;
    transition: color 0.2s ease;

    &:hover {
        color: #ef4444;
    }
`;

const EmptyHint = styled.p`
    color: ${RebelColors.textMuted};
    font-size: 0.85rem;
    font-style: italic;
    text-align: center;
    margin: 16px 0 0 0;
`;
